import { useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  checkIsAllDomainsSelected,
  getNodeDescendantIds,
  getNodeWithDescendantsOptions,
  getSelectedDomains,
} from '@components/EntityDrawers/components/inputs/DomainDropdown/helpers';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import { ISelectOption } from '@constants/entities/ui';
import useProjectId from '@hooks/useProjectId';
import { Checkbox, Stack, Typography } from '@mui/material';
import { TreeView } from '@mui/x-tree-view';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { renderTree } from '@views/Project/components/ScopeFilterModal/ScopeFilterModal';
import { SelectHandler } from '@views/Project/components/ScopeFilterModal/TreeItem';
import { buildTree } from '@views/Project/helpers';

const DomainsPaperBody = () => {
  const projectId = useProjectId();
  const { data: projectDomains } = useGetProjectDomainsQuery(
    projectId || skipToken,
  );
  const [expanded, setExpanded] = useState<string[]>([]);

  /** Cache of individual domain selections before enterprise was toggled on */
  const cachedDomainsRef = useRef<ISelectOption[]>([]);

  const [wideDomain] = useMemo(() => {
    return buildTree(projectDomains ?? []);
  }, [projectDomains]);

  const nonEnterpriseTopLevelCount = wideDomain?.children?.length ?? 0;
  const hasWideDomain = !!wideDomain;
  const shouldAutoCollapseToWide = nonEnterpriseTopLevelCount > 1;

  const buildAllDomainOptions = (): ISelectOption[] =>
    (wideDomain?.children ?? []).flatMap((child) =>
      getNodeWithDescendantsOptions(wideDomain, child.id),
    );

  const hanleClick = (nodeId: string) => {
    setExpanded((ids) =>
      ids.includes(nodeId)
        ? ids.filter((id) => id !== nodeId)
        : [...ids, nodeId],
    );
  };

  const handleSelect =
    (onChange: (value: any) => void, currentValue: any): SelectHandler =>
    ({ nodeId, checked }) => {
      const options: ISelectOption[] = currentValue ?? [];
      const isCurrentlyWideSelected = options.some(
        (d) => d.value === wideDomain?.id,
      );

      setExpanded((prev) =>
        Array.from(
          new Set([...prev, ...getNodeDescendantIds(wideDomain, nodeId)]),
        ),
      );

      if (isCurrentlyWideSelected) {
        cachedDomainsRef.current = [];
      }

      const optionsById = new Map<string, ISelectOption>(
        (isCurrentlyWideSelected ? buildAllDomainOptions() : options).map(
          (option) => [String(option.value), option],
        ),
      );

      if (checked) {
        getNodeWithDescendantsOptions(wideDomain, nodeId).forEach((option) =>
          optionsById.set(String(option.value), option),
        );
      } else {
        optionsById.delete(nodeId);
      }

      optionsById.delete(String(wideDomain?.id));

      const newDomainsArray = Array.from(optionsById.values());

      const { isAllDomainsSelected } = checkIsAllDomainsSelected({
        projectDomains: projectDomains ?? [],
        selectedDomains: newDomainsArray,
      });

      if (isAllDomainsSelected && shouldAutoCollapseToWide) {
        cachedDomainsRef.current = [];
        onChange([{ value: wideDomain.id, label: wideDomain.name }]);
        return;
      }

      onChange(newDomainsArray);
    };

  if (!hasWideDomain) return null;

  return (
    <Stack>
      <Controller
        render={({ field: { value, onChange } }) => {
          const isWideSelected = value?.some(
            (domain: ISelectOption) => domain.value === wideDomain.id,
          );

          const selectedDomains = getSelectedDomains({
            selectedDomains: value,
            wideDomain,
          });

          return (
            <>
              <Stack
                direction="row"
                pl={1.5}
                pr={3}
                py={2}
                alignItems="center"
                gap={2}
              >
                <Checkbox
                  checked={isWideSelected}
                  onChange={(_, checked) => {
                    if (checked) {
                      cachedDomainsRef.current = (value ?? []).filter(
                        (d: ISelectOption) => d.value !== wideDomain.id,
                      );
                      onChange([
                        {
                          value: wideDomain.id,
                          label: wideDomain.name,
                        },
                      ]);
                    } else {
                      onChange(cachedDomainsRef.current);
                      cachedDomainsRef.current = [];
                    }
                  }}
                />

                <Typography color="grey.900">{wideDomain.name}</Typography>
              </Stack>

              <TreeView
                expanded={expanded}
                disableSelection
                multiSelect
                selected={selectedDomains}
                sx={{ pl: 1.5 }}
              >
                {wideDomain.children?.map((item) =>
                  renderTree(
                    item,
                    [],
                    handleSelect(onChange, value),
                    hanleClick,
                    undefined,
                    {
                      p: '8px 12px',
                    },
                  ),
                )}
              </TreeView>
            </>
          );
        }}
        name={CommonKeys.Domains}
      />
    </Stack>
  );
};

export default DomainsPaperBody;
